import { useEffect, useRef, useState } from "react";
import { profileService } from "@/services/profileService";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

interface UseProfileAutoSaveProps {
  onSave: (data: { firstName: string; lastName: string }) => Promise<boolean>;
  delay?: number;
}

export const useProfileAutoSave = ({
  onSave,
  delay = 2000,
}: UseProfileAutoSaveProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const { toast } = useToast();
  const { refreshProfile } = useAuth();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingDataRef = useRef<{ firstName: string; lastName: string } | null>(
    null,
  );
  const isMountedRef = useRef(true);

  const triggerSave = async (data: { firstName: string; lastName: string }) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Store the pending data
    pendingDataRef.current = data;

    // Set a new timeout
    timeoutRef.current = setTimeout(async () => {
      if (!pendingDataRef.current || !isMountedRef.current) return;

      if (isMountedRef.current) {
        setIsSaving(true);
      }

      try {
        const success = await onSave(pendingDataRef.current);
        if (success && isMountedRef.current) {
          setLastSaved(new Date());
          await refreshProfile(); // Refresh auth context
        }
      } catch (error) {
        console.error("Auto-save error:", error);
        if (isMountedRef.current) {
          toast({
            variant: "destructive",
            title: "Auto-save failed",
            description: "Failed to save your changes. Please try again.",
          });
        }
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
        pendingDataRef.current = null;
      }
    }, delay);
  };

  const triggerSaveImmediate = async (data: {
    firstName: string;
    lastName: string;
  }) => {
    // Cancel pending autosave
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingDataRef.current = null;

    try {
      setIsSaving(true);
      const success = await onSave(data);
      if (success) {
        setLastSaved(new Date());
        await refreshProfile();
      }
    } catch (error) {
      console.error("Immediate save error:", error);
      toast({
        variant: "destructive",
        title: "Save failed",
        description: "Failed to save your changes. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Cleanup timeout and mark as unmounted
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    triggerSave,
    triggerSaveImmediate,
    isSaving,
    lastSaved,
  };
};
